import {
	MtmaDiagnosticsEventType,
	MtmaDiagnosticsErrorSubType,
	MtmaDiagnosticsInfoSubType,
} from 'src/app/model/mtma-diagnostics-log';

export type ValueDisplayDescriptor =
	| { kind: 'i18n'; key: string; params?: Record<string, string | number> }
	| { kind: 'raw'; text: string }
	| { kind: 'empty' };

const KNOWN_ERROR_SUB_TYPES = new Set<number>([
	MtmaDiagnosticsErrorSubType.NoSimInserted,
	MtmaDiagnosticsErrorSubType.SimLockedByPin,
	MtmaDiagnosticsErrorSubType.WrongPinEntered,
	MtmaDiagnosticsErrorSubType.SimLockedByPuk,
	MtmaDiagnosticsErrorSubType.NoServiceAvailable,
	MtmaDiagnosticsErrorSubType.NoApnStored,
	MtmaDiagnosticsErrorSubType.WrongApn,
	MtmaDiagnosticsErrorSubType.NoConfigurationStored,
	MtmaDiagnosticsErrorSubType.NtpTimeError,
	MtmaDiagnosticsErrorSubType.SslConnectionError,
	MtmaDiagnosticsErrorSubType.MqttConnectionError,
	MtmaDiagnosticsErrorSubType.AutoprovisioningFailed,
	MtmaDiagnosticsErrorSubType.ProgrammingFailed,
	MtmaDiagnosticsErrorSubType.UpdateFailed,
	MtmaDiagnosticsErrorSubType.CallAccessEventsMemoryFull,
]);

const KNOWN_INFO_SUB_TYPES = new Set<number>([
	MtmaDiagnosticsInfoSubType.GeneralState,
	MtmaDiagnosticsInfoSubType.ResetCause,
	MtmaDiagnosticsInfoSubType.FactoryDefaults,
	MtmaDiagnosticsInfoSubType.NewOperator,
	MtmaDiagnosticsInfoSubType.CurrentApnCleared,
	MtmaDiagnosticsInfoSubType.NetModeChange,
	MtmaDiagnosticsInfoSubType.NewSmsProcessed,
	MtmaDiagnosticsInfoSubType.NewSmsDiscarded,
	MtmaDiagnosticsInfoSubType.AutoprovisioningStarted,
	MtmaDiagnosticsInfoSubType.AutoprovisioningCompleted,
	MtmaDiagnosticsInfoSubType.ProgrammingStarted,
	MtmaDiagnosticsInfoSubType.ProgrammingCompleted,
	MtmaDiagnosticsInfoSubType.UpdateStarted,
	MtmaDiagnosticsInfoSubType.UpdateCompleted,
	MtmaDiagnosticsInfoSubType.UtcTimeRetrieved,
]);

function normalizeSubType(subType: number): number {
	return Math.trunc(Number(subType));
}

function coerceDiagnosticsInt(value: unknown): number | null {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return Math.trunc(value);
	}
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (trimmed === '') {
			return null;
		}
		const n = Number.parseInt(trimmed, 10);
		if (!Number.isNaN(n)) {
			return n;
		}
	}
	return null;
}

/**
 * ngx-translate key for the event sub-type label, or null to show the raw number.
 */
export function getMtmaDiagnosticsSubTypeKey(type: number, subType: number): string | null {
	const st = normalizeSubType(subType);
	if (type === MtmaDiagnosticsEventType.Error) {
		return KNOWN_ERROR_SUB_TYPES.has(st) ? `MTMA_DIAG_ERR_ST_${st}` : null;
	}
	if (type === MtmaDiagnosticsEventType.Info) {
		return KNOWN_INFO_SUB_TYPES.has(st) ? `MTMA_DIAG_INFO_ST_${st}` : null;
	}
	return null;
}

const RESET_CAUSE_KEYS: Record<number, string> = {
	0: 'MTMA_DIAG_VAL_RESET_0',
	1: 'MTMA_DIAG_VAL_RESET_1',
	3: 'MTMA_DIAG_VAL_RESET_3',
	4: 'MTMA_DIAG_VAL_RESET_4',
	7: 'MTMA_DIAG_VAL_RESET_7',
	16: 'MTMA_DIAG_VAL_RESET_16',
};

/**
 * How to render the Value column (excluding Info + GeneralState which uses row expansion).
 */
export function getMtmaDiagnosticsValueDescriptor(
	type: number,
	subType: number,
	rawValue: string | undefined | null,
): ValueDisplayDescriptor {
	const st = normalizeSubType(subType);
	const raw = rawValue === undefined || rawValue === null ? '' : String(rawValue);

	if (type === MtmaDiagnosticsEventType.Error) {
		switch (st) {
			case MtmaDiagnosticsErrorSubType.WrongApn:
				return { kind: 'i18n', key: 'MTMA_DIAG_FMT_APN', params: { v: raw.trim() } };
			case MtmaDiagnosticsErrorSubType.ProgrammingFailed: {
				const n = coerceDiagnosticsInt(raw);
				if (n !== null && n >= 0 && n <= 17) {
					return { kind: 'i18n', key: `MTMA_DIAG_VAL_PROG_FAIL_${n}` };
				}
				return { kind: 'raw', text: raw };
			}
			case MtmaDiagnosticsErrorSubType.UpdateFailed: {
				const n = coerceDiagnosticsInt(raw);
				if (n === 0) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_UPD_FAIL_0' };
				}
				if (n === 1) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_UPD_FAIL_1' };
				}
				if (n !== null && n >= 2) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_FW_UPDATE_ERR_CODE', params: { n } };
				}
				return { kind: 'raw', text: raw };
			}
			default:
				if (KNOWN_ERROR_SUB_TYPES.has(st)) {
					return { kind: 'empty' };
				}
				return { kind: 'raw', text: raw };
		}
	}

	if (type === MtmaDiagnosticsEventType.Info) {
		switch (st) {
			case MtmaDiagnosticsInfoSubType.GeneralState:
				return { kind: 'empty' };
			case MtmaDiagnosticsInfoSubType.ResetCause: {
				const n = coerceDiagnosticsInt(raw);
				if (n === null) {
					return { kind: 'raw', text: raw };
				}
				const key = RESET_CAUSE_KEYS[n];
				if (key) {
					return { kind: 'i18n', key };
				}
				return { kind: 'raw', text: String(n) };
			}
			case MtmaDiagnosticsInfoSubType.FactoryDefaults:
			case MtmaDiagnosticsInfoSubType.CurrentApnCleared:
			case MtmaDiagnosticsInfoSubType.ProgrammingStarted:
			case MtmaDiagnosticsInfoSubType.ProgrammingCompleted:
			case MtmaDiagnosticsInfoSubType.UpdateCompleted:
				return { kind: 'empty' };
			case MtmaDiagnosticsInfoSubType.NewOperator:
				return { kind: 'i18n', key: 'MTMA_DIAG_FMT_OPERATOR', params: { v: raw.trim() } };
			case MtmaDiagnosticsInfoSubType.NetModeChange: {
				const n = coerceDiagnosticsInt(raw);
				if (n === 0 || n === 1 || n === 2) {
					return { kind: 'i18n', key: `MTMA_DIAG_INFO_VAL_NETMODE_${n}` };
				}
				return { kind: 'raw', text: raw };
			}
			case MtmaDiagnosticsInfoSubType.NewSmsProcessed:
			case MtmaDiagnosticsInfoSubType.NewSmsDiscarded:
				return { kind: 'i18n', key: 'MTMA_DIAG_FMT_SENDER', params: { v: raw.trim() } };
			case MtmaDiagnosticsInfoSubType.AutoprovisioningStarted: {
				const n = coerceDiagnosticsInt(raw);
				if (n === 0) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_AP_STARTED_0' };
				}
				if (n === 1) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_AP_STARTED_1' };
				}
				return { kind: 'raw', text: raw };
			}
			case MtmaDiagnosticsInfoSubType.AutoprovisioningCompleted: {
				const n = coerceDiagnosticsInt(raw);
				if (n === 0) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_CERT_0' };
				}
				if (n === 1) {
					return { kind: 'i18n', key: 'MTMA_DIAG_VAL_CERT_1' };
				}
				return { kind: 'raw', text: raw };
			}
			case MtmaDiagnosticsInfoSubType.UpdateStarted: {
				const n = coerceDiagnosticsInt(raw);
				if (n !== null && n >= 0 && n <= 7) {
					return { kind: 'i18n', key: `MTMA_DIAG_VAL_UPDATE_STARTED_${n}` };
				}
				return { kind: 'raw', text: raw };
			}
			case MtmaDiagnosticsInfoSubType.UtcTimeRetrieved:
				return { kind: 'i18n', key: 'MTMA_DIAG_FMT_TIMEZONE', params: { v: raw.trim() } };
			default:
				if (KNOWN_INFO_SUB_TYPES.has(st)) {
					return { kind: 'empty' };
				}
				return { kind: 'raw', text: raw };
		}
	}

	return { kind: 'raw', text: raw };
}
